import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, FileText, Filter, Calendar, BarChart, GraduationCap, Lock, UserPlus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const HERO_BG = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";

// Login Prompt Modal Component
const LoginPromptModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border border-primary/30 rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Icon */}
        <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
          <Lock className="h-8 w-8 text-primary" />
        </div>

        {/* Content */}
        <div className="text-center">
          <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
            Members-Only Resource
          </h3>
          <p className="text-muted-foreground mb-6">
            Join AKIT to download this resource and access our complete library of teaching materials, 
            exam papers, and curriculum guides.
          </p>

          {/* Benefits */}
          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
            <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-3">
              Member Benefits Include:
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Download className="h-4 w-4 text-primary" />
                Unlimited resource downloads
              </li>
              <li className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                Access to all exam papers & notes
              </li>
              <li className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-primary" />
                C.B.E Curriculum materials
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="flex-1">
              <Button className="w-full btn-primary" data-testid="modal-register-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Join AKIT Free
              </Button>
            </Link>
            <Link to="/login" className="flex-1">
              <Button variant="outline" className="w-full border-white/20" data-testid="modal-login-btn">
                Login
              </Button>
            </Link>
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            Registration is free and takes less than a minute
          </p>
        </div>
      </div>
    </div>
  );
};

export const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { API, BACKEND_URL, isAuthenticated } = useAuth();

  const categories = [
    { value: 'all', label: 'All Resources' },
    { value: 'exams', label: 'Exams' },
    { value: 'teaching_notes', label: 'Teaching Notes' },
    { value: 'schemes_of_work', label: 'Schemes of Work' },
    { value: 'cbe_curriculum', label: 'C.B.E Curriculum' },
  ];

  // ✅ UPDATED: Added Pre-primary levels
  const levels = {
    preprimary: ['Pre-primary 1', 'Pre-primary 2'],
    secondary: ['Form 1', 'Form 2', 'Form 3', 'Form 4'],
    cbe: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 
          'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'],
  };

  useEffect(() => {
    fetchResources();
  }, [selectedCategory, selectedLevel]);

  const fetchResources = async () => {
    try {
      let url = `${API}/resources`;
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedLevel) {
        params.append('level', selectedLevel);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      setResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDownload = (resource) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    window.open(`${BACKEND_URL}${resource.file_url}`, '_blank');
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return 'PDF';
      case 'doc':
      case 'docx':
        return 'DOC';
      default:
        return 'FILE';
    }
  };

  const getCategoryIcon = (category) => {
    if (category === 'cbe_curriculum') {
      return <GraduationCap className="h-6 w-6 text-primary" />;
    }
    return <FileText className="h-6 w-6 text-primary" />;
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Login Prompt Modal */}
      <LoginPromptModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />

      {/* Hero Section */}
      <section 
        className="relative py-20"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-2">
            <p className="text-primary font-amiri text-xl mb-2">بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم</p>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-4 text-center">
            IRE Resources Library
          </h1>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
            Access comprehensive teaching materials, exam papers, and educational resources 
            for Islamic Religious Education — including the new <span className="text-primary font-semibold">C.B.E Curriculum</span> for Pre-primary to Grade 12.
          </p>

          {/* Login Notice for non-authenticated users */}
          {!isAuthenticated && (
            <div className="max-w-2xl mx-auto mb-6">
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground font-medium">Members-Only Downloads</p>
                  <p className="text-xs text-muted-foreground">Join AKIT (free) to download resources</p>
                </div>
                <Link to="/register">
                  <Button size="sm" className="btn-primary">
                    Join Free
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search resources, exams, or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-glass pl-12 h-14 text-lg"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-wrap gap-3 items-center">
            <Filter className="h-5 w-5 text-muted-foreground" />
            
            {/* Category Filters */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {categories.map((cat) => (
                <Button
                  key={cat.value}
                  variant={selectedCategory === cat.value ? 'default' : 'outline'}
                  className={`whitespace-nowrap ${selectedCategory === cat.value ? 'btn-primary' : 'border-white/10'}`}
                  onClick={() => {
                    setSelectedCategory(cat.value);
                    setSelectedLevel(''); // Reset level when category changes
                  }}
                  data-testid={`filter-${cat.value}`}
                >
                  {cat.value === 'cbe_curriculum' && <GraduationCap className="h-4 w-4 mr-1" />}
                  {cat.label}
                </Button>
              ))}
            </div>

            {/* ✅ UPDATED: Level Filter with Pre-primary */}
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="h-9 px-4 rounded-full bg-secondary border border-white/10 text-sm text-foreground"
              data-testid="level-filter"
            >
              <option value="">All Levels</option>
              {selectedCategory === 'cbe_curriculum' ? (
                // Show only CBE levels (including Pre-primary) when CBE category is selected
                <>
                  <optgroup label="Pre-Primary">
                    {levels.preprimary.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Lower Primary (Grade 1-3)">
                    {['Grade 1', 'Grade 2', 'Grade 3'].map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Upper Primary (Grade 4-6)">
                    {['Grade 4', 'Grade 5', 'Grade 6'].map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Junior Secondary (Grade 7-9)">
                    {['Grade 7', 'Grade 8', 'Grade 9'].map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Senior Secondary (Grade 10-12)">
                    {['Grade 10', 'Grade 11', 'Grade 12'].map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                </>
              ) : (
                // Show all levels for other categories
                <>
                  <optgroup label="Pre-Primary">
                    {levels.preprimary.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Secondary (8-4-4)">
                    {levels.secondary.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                  <optgroup label="C.B.E Curriculum">
                    {levels.cbe.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </optgroup>
                </>
              )}
            </select>
          </div>
        </div>
      </section>

      {/* C.B.E Info Banner (show when CBE category selected) */}
      {selectedCategory === 'cbe_curriculum' && (
        <section className="py-4 bg-primary/10 border-b border-primary/20">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-primary" />
              <p className="text-sm text-foreground">
                <span className="font-semibold text-primary">Competency Based Education (C.B.E)</span> — 
                Resources aligned with Kenya's new curriculum from Pre-primary to Grade 12
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Resources Grid */}
      <section className="py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-bold text-primary">
              {selectedCategory === 'cbe_curriculum' ? 'C.B.E Resources' : 'Available Documents'}
            </h2>
            <span className="text-sm text-muted-foreground">
              {filteredResources.length} items
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-muted-foreground mt-4">Loading resources...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-2xl">
              {selectedCategory === 'cbe_curriculum' ? (
                <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              ) : (
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              )}
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Resources Found</h3>
              <p className="text-muted-foreground">
                {searchQuery 
                  ? 'Try adjusting your search terms' 
                  : selectedCategory === 'cbe_curriculum'
                    ? 'C.B.E Curriculum resources will be available soon. Check back later!'
                    : 'No resources available in this category'
                }
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredResources.map((resource) => (
                <div 
                  key={resource.id}
                  className="glass-card rounded-xl p-5 hover:border-primary/50 transition-all"
                  data-testid={`resource-card-${resource.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex gap-4 flex-1">
                      <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                        {getCategoryIcon(resource.category)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-heading font-bold text-foreground truncate">{resource.title}</h3>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-primary/20 text-primary uppercase">
                            {getFileIcon(resource.file_type)}
                          </span>
                          {resource.category === 'cbe_curriculum' && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-green-500/20 text-green-400 uppercase">
                              C.B.E
                            </span>
                          )}
                          {/* ✅ NEW: Pre-primary badge */}
                          {resource.level?.includes('Pre-primary') && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 uppercase">
                              Pre-Primary
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{resource.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                          {resource.year && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Year: {resource.year}
                            </span>
                          )}
                          {resource.level && (
                            <span className="flex items-center gap-1">
                              <BarChart className="h-3 w-3" />
                              {resource.level}
                            </span>
                          )}
                          <span className="text-primary/70">
                            {getCategoryLabel(resource.category)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleDownload(resource)}
                      className={`flex-shrink-0 ${isAuthenticated ? 'btn-primary' : 'bg-gray-600 hover:bg-gray-500'}`}
                      data-testid={`download-${resource.id}`}
                    >
                      {isAuthenticated ? (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Login to Download
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
